import React, { useEffect } from 'react';
import { Table, Button, Space, Tag, Popconfirm, message, Tooltip } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  DownloadOutlined,
  EyeOutlined,
  DeleteOutlined,
  FileOutlined,
  FilePdfOutlined,
  FileImageOutlined,
  FileWordOutlined,
  FileExcelOutlined,
} from '@ant-design/icons';
import { useDocumentsByEntity, useDeleteDocument } from '../../hooks/useDocuments';
import { documentService, Document } from '../../services/document.service';
import { useAuthStore } from '../../store/authStore';
import dayjs from 'dayjs';
import { API_BASE_URL } from '@/config/apiBaseUrl';

interface DocumentListProps {
  entityType: string;
  entityId: string;
  showUpload?: boolean;
  onUploadClick?: () => void;
  onRefetch?: (refetchFn: () => void) => void;
}

const DocumentList: React.FC<DocumentListProps> = ({
  entityType,
  entityId,
  showUpload = false,
  onUploadClick,
  onRefetch,
}) => {
  const { data: documents = [], isLoading, refetch } = useDocumentsByEntity(entityType, entityId);
  const deleteMutation = useDeleteDocument();
  const { user } = useAuthStore();

  // Expose refetch function to parent
  useEffect(() => {
    if (onRefetch) {
      onRefetch(refetch);
    }
  }, [onRefetch, refetch]);

  // Refetch when entityId changes
  useEffect(() => {
    if (entityType && entityId) {
      refetch();
    }
  }, [entityType, entityId, refetch]);

  // Listen for custom event to refetch (can be triggered from parent)
  useEffect(() => {
    const handleRefetch = () => {
      refetch();
    };
    
    const eventName = `refetch-documents-${entityType}-${entityId}`;
    window.addEventListener(eventName, handleRefetch);
    
    return () => {
      window.removeEventListener(eventName, handleRefetch);
    };
  }, [entityType, entityId, refetch]);

  const getFileIcon = (fileType?: string, mimeType?: string) => {
    if (!fileType && !mimeType) return <FileOutlined />;
    
    const type = (fileType || mimeType || '').toLowerCase();
    if (type.includes('pdf')) return <FilePdfOutlined style={{ color: '#ff4d4f' }} />;
    if (type.includes('image')) return <FileImageOutlined style={{ color: '#52c41a' }} />;
    if (type.includes('word') || type.includes('doc')) return <FileWordOutlined style={{ color: '#145A54' }} />;
    if (type.includes('excel') || type.includes('xls')) return <FileExcelOutlined style={{ color: '#52c41a' }} />;
    return <FileOutlined />;
  };

  const handleDownload = async (doc: Document) => {
    try {
      // Get the API base URL
      const apiUrl = API_BASE_URL;
      const token = localStorage.getItem('token');
      
      // Use fetch directly to get the blob
      const response = await fetch(`${apiUrl}/documents/${doc.id}/download`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = doc.file_name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      message.success('Document downloaded');
      refetch();
    } catch (error) {
      console.error('Download error:', error);
      message.error('Failed to download document');
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteMutation.mutateAsync(id);
      refetch();
    } catch (error) {
      console.error('Delete error:', error);
    }
  };

  const canDeleteAttachment = (record: Document) => {
    if (!user?.id) return false;
    if (user.is_super_admin) return true;
    const perms = user.permissions ?? [];
    if (perms.includes('document.delete')) return true;
    if (!record.uploaded_by) return true;
    return String(user.id).toLowerCase() === String(record.uploaded_by).toLowerCase();
  };

  const columns: ColumnsType<Document> = [
    {
      title: 'Document',
      key: 'document',
      render: (_, record) => (
        <Space>
          {getFileIcon(record.file_type, record.mime_type)}
          <div>
            <div style={{ fontWeight: 500 }}>{record.document_name}</div>
            <div style={{ fontSize: '12px', color: '#8c8c8c' }}>
              {record.document_number} • {documentService.formatFileSize(record.file_size_bytes)}
            </div>
          </div>
        </Space>
      ),
    },
    {
      title: 'Type',
      dataIndex: 'document_type',
      key: 'document_type',
      render: (type) => type ? <Tag>{type}</Tag> : '-',
    },
    {
      title: 'Status',
      dataIndex: 'document_status',
      key: 'document_status',
      render: (status) => {
        const colors: Record<string, string> = {
          Active: 'success',
          Draft: 'default',
          Archived: 'warning',
          Deleted: 'error',
        };
        return <Tag color={colors[status] || 'default'}>{status}</Tag>;
      },
    },
    {
      title: 'Uploaded',
      key: 'uploaded',
      render: (_, record) => (
        <div>
          <div>{dayjs(record.created_at).format('DD/MM/YYYY')}</div>
          <div style={{ fontSize: '12px', color: '#8c8c8c' }}>
            {record.view_count} views • {record.download_count} downloads
          </div>
        </div>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Tooltip title="View">
            <Button
              type="link"
              icon={<EyeOutlined />}
              onClick={async () => {
                try {
                  // Get the API base URL
                  const apiUrl = API_BASE_URL;
                  const token = localStorage.getItem('token');
                  
                  // Construct the view URL with authentication
                  const viewUrl = `${apiUrl}/documents/${record.id}/view`;
                  
                  // Open in new window - browser will handle authentication via cookies or we can add token
                  // For now, use a direct link approach
                  const link = document.createElement('a');
                  link.href = viewUrl;
                  link.target = '_blank';
                  link.rel = 'noopener noreferrer';
                  
                  // Add authorization header via fetch and create blob URL
                  const response = await fetch(viewUrl, {
                    headers: {
                      'Authorization': `Bearer ${token}`,
                    },
                  });
                  
                  if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                  }
                  
                  const blob = await response.blob();
                  const url = window.URL.createObjectURL(blob);
                  window.open(url, '_blank');
                  
                  // Clean up the URL after a delay
                  setTimeout(() => window.URL.revokeObjectURL(url), 100);
                } catch (error) {
                  console.error('View error:', error);
                  message.error('Failed to open document');
                }
              }}
            />
          </Tooltip>
          <Tooltip title="Download">
            <Button
              type="link"
              icon={<DownloadOutlined />}
              onClick={() => handleDownload(record)}
            />
          </Tooltip>
          {canDeleteAttachment(record) ? (
            <Popconfirm
              title="Are you sure you want to delete this document?"
              onConfirm={() => handleDelete(record.id)}
              okText="Yes"
              cancelText="No"
            >
              <Tooltip title="Delete">
                <Button type="link" danger icon={<DeleteOutlined />} />
              </Tooltip>
            </Popconfirm>
          ) : null}
        </Space>
      ),
    },
  ];

  return (
    <div>
      {showUpload && onUploadClick && (
        <div style={{ marginBottom: 16 }}>
          <Button type="primary" onClick={onUploadClick}>
            Upload Document
          </Button>
        </div>
      )}
      <div style={{ marginBottom: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontSize: '12px', color: '#8c8c8c' }}>
          {documents.length} document{documents.length !== 1 ? 's' : ''} found
        </span>
        <Button size="small" onClick={() => refetch()} loading={isLoading}>
          Refresh
        </Button>
      </div>
      <Table
        columns={columns}
        dataSource={documents}
        loading={isLoading}
        rowKey="id"
        pagination={{ pageSize: 10 }}
        locale={{ emptyText: 'No documents found' }}
      />
    </div>
  );
};

export default DocumentList;

